/**
 * EditableNutrientTable
 *
 * Tabular grouped layout used by the Update / Add Food Entry forms. Mirrors
 * the read-only DetailedNutrientsPanel used on Diet Recall and Meal Plan,
 * but with editable amount inputs and per-row commit/revert actions.
 *
 * Per-row UX:
 *   - Always editable (no lock/unlock).
 *   - When the current value differs from the saved original, the row is
 *     "dirty" — a Check button (commit just this row) and an X button
 *     (revert to last saved value) appear at the right of the input.
 *   - Commits are optimistic at the parent level: parent treats the value
 *     as saved the instant Check is clicked and fires the PATCH in the
 *     background. This component is purely visual and dispatches callbacks.
 *
 * RDA % is intentionally NOT shown here — meaningless on a per-100g food
 * entry. See .planning/food-library-crud-fix.md (Step 8).
 */
import * as React from "react";
import { Check, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { roundNutrientInput } from "@/lib/nutrition-form-utils";
import { cn } from "@/lib/utils";

export interface EditableNutrientField {
  name: string;
  label: string;
  unit: string;
}

interface EditableNutrientTableProps {
  title: string;
  fields: EditableNutrientField[];
  values: Record<string, string>;
  dirty: Set<string>;
  /**
   * Optional per-field unit overrides, sourced from backend on the read path.
   */
  unitOverrides?: Record<string, string>;
  /**
   * Optional per-field label overrides, sourced from backend on the read path.
   */
  labelOverrides?: Record<string, string>;
  onChange: (fieldName: string, value: string) => void;
  onCommit: (fieldName: string) => void;
  onRevert: (fieldName: string) => void;
  disabled?: boolean;
  /**
   * When true, suppress the per-row Check / X commit and revert buttons.
   * The dirty-row tint still applies, so callers using batch submission
   * (e.g. AddFoodEntryForm) get visual feedback on filled rows without
   * exposing single-row mutation handlers.
   */
  hideRowActions?: boolean;
}

interface AmountInputProps {
  fieldName: string;
  value: string;
  unit?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

// Amount input with its unit suffix. On blur the value is rounded to the
// field's precision (Energy whole, others 2dp) so what is saved matches what
// the screens display.
const AmountInput = ({
  fieldName,
  value,
  unit,
  disabled,
  onChange,
}: AmountInputProps) => {
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const rounded = roundNutrientInput(e.target.value, fieldName);
    if (rounded !== e.target.value) onChange(rounded);
  };

  return (
    <div className="relative w-32">
      <Input
        value={value}
        placeholder="—"
        inputMode="decimal"
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        className={cn("h-8 text-right tabular-nums", unit ? "pr-9" : "pr-2")}
      />
      {unit && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 z-hint text-muted-foreground pointer-events-none">
          {unit}
        </span>
      )}
    </div>
  );
};

export const EditableNutrientTable: React.FC<EditableNutrientTableProps> = ({
  title,
  fields,
  values,
  dirty,
  unitOverrides,
  labelOverrides,
  onChange,
  onCommit,
  onRevert,
  disabled,
  hideRowActions,
}) => {
  if (fields.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="bg-muted/50 border-b border-border px-3 py-2">
        <h4 className="z-label text-foreground">{title}</h4>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-border/60 hover:bg-transparent">
            <TableHead className="z-eyebrow text-muted-foreground h-8 px-3">
              Nutrient
            </TableHead>
            <TableHead className="z-eyebrow text-muted-foreground h-8 px-3 text-right">
              Amount
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field) => {
            const value = values[field.name] ?? "";
            const isDirty = dirty.has(field.name);
            const displayLabel = labelOverrides?.[field.name] ?? field.label;
            const displayUnit = unitOverrides?.[field.name] ?? field.unit;

            return (
              <TableRow
                key={field.name}
                className={cn(
                  "border-border/60",
                  isDirty ? "bg-warning/10" : "hover:bg-muted/20"
                )}
              >
                <TableCell
                  className="px-3 py-2 z-caption text-foreground"
                  title={displayLabel}
                >
                  {displayLabel}
                </TableCell>
                <TableCell className="px-3 py-2">
                  <div className="ml-auto flex w-fit items-center justify-end gap-1">
                    <AmountInput
                      fieldName={field.name}
                      value={value}
                      unit={displayUnit}
                      disabled={disabled}
                      onChange={(next) => onChange(field.name, next)}
                    />
                    {isDirty && !hideRowActions && (
                      <>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-success hover:text-success hover:bg-success/10"
                          onClick={() => onCommit(field.name)}
                          disabled={disabled}
                          title="Commit this value"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          onClick={() => onRevert(field.name)}
                          disabled={disabled}
                          title="Revert to last saved value"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
